import { Redirect } from "expo-router";
import { Pressable, StyleSheet, View } from "react-native";
import { IconSymbol } from "@/components/ui/IconSymbol";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import Search from "@/components/Search";
import { useState, useRef, useEffect } from "react";
import { Image, type ImageSource } from "expo-image";
import { ThemedText } from "@/components/ThemedText";
import Avatar from "@/components/Avatar";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Drawer } from "expo-router/drawer";
import { DrawerContentScrollView, DrawerItem, DrawerItemList } from "@react-navigation/drawer";
import { DrawerActions } from "@react-navigation/native";
import useContextMenu from "@/hooks/useContextMenu";
import { Button } from "@/components/ui/Button";
import { supabase } from "@/lib/supabase-client";
import { useStorage } from "@/hooks/useStorage";

export default function CrewLayout() {
  const { session, loading, signOut, userRoles, displayNames } = useAuth();
  const { colors } = useTheme();
  const contextMenu = useContextMenu();
  const storage = useStorage();

  const [width, setWidth] = useState(1800);
  const [logo] = useState<ImageSource>(require("@/assets/images/rockforce-logo-rf-yellow.svg"));
  const [profileMenuVisible, setProfileMenuVisible] = useState(false);
  const profileButtonRef = useRef<View>(null);
  const [userName, setUserName] = useState<string>("Username");
  const [shiftsLabel, setShiftsLabel] = useState<string>(() => storage.getString("shiftsLabelPlural") || "Shifts");

  // Update shifts label from displayNames and cache it
  useEffect(() => {
    const newLabel = displayNames.shifts.plural;
    if (newLabel && newLabel !== shiftsLabel) {
      setShiftsLabel(newLabel);
      storage.saveFast("shiftsLabelPlural", newLabel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayNames.shifts.plural]);

  useEffect(() => {
    const fetchUserName = async () => {
      if (!session?.user.id) return;

      const { data, error } = await supabase
        .from("users")
        .select("first_name, preferred_name")
        .eq("id", session.user.id)
        .single();

      if (data && !error) {
        setUserName(data.preferred_name || data.first_name || "Username");
      }
    };

    fetchUserName();
  }, [session?.user.id]);

  if (!session && !loading) return <Redirect href="/sign-in" />;

  // if (!loading && hasAdminPortal(userRoles)) {
  //   return <Redirect href="/(app)/(root)" />;
  // }

  const isDesktopLayout = width >= 900;
  const hideLabels = width <= 1600 && isDesktopLayout;
  const hideRightPanel = width <= 1000;

  // Context menu content for profile
  const profileMenuContent = (
    <View style={{ minWidth: 180, backgroundColor: colors.card, borderRadius: 8, padding: 4 }}>
      <Button
        style={{
          justifyContent: "flex-start",
          backgroundColor: "transparent",
          borderColor: "transparent",
        }}
        text="Sign Out"
        icon="logout"
        iconPosition="left"
        onPress={() => {
          contextMenu.setVisible(false);
          signOut();
        }}
      />
    </View>
  );

  return (
    <Drawer
      backBehavior="order"
      drawerContent={(props) => (
        <DrawerContentScrollView style={{ height: "100%" }} contentContainerStyle={{ height: "100%" }}>
          <View
            style={{
              gap: 8,
              marginVertical: 16,
              flexDirection: "row",
              justifyContent: "flex-start",
              alignContent: "center",
              alignItems: "center",
            }}
          ></View>
          {isDesktopLayout && (
            <DrawerItem
              icon={() => <Image source={[logo, { width: 100 }]} style={{ height: 34, width: 34, left: -5 }} />}
              labelStyle={{ fontSize: 24, color: colors.text }}
              label="NEXUS"
              onPress={() => null}
            />
          )}
          <DrawerItemList {...props} />
          <View style={{ position: "absolute", bottom: 10 }} ref={profileButtonRef}>
            <DrawerItem
              icon={() => <Avatar userId={session?.user.id} size={44} style={{ position: "absolute" }} />}
              label={() => (
                <View style={{ flexDirection: "row", gap: 16 }}>
                  {hideLabels ? null : (
                    <ThemedText style={{ marginLeft: 48 }} type="subtitle">
                      {userName}
                    </ThemedText>
                  )}
                  {hideLabels ? null : <MaterialCommunityIcons color={colors.text} name="dots-horizontal" size={28} />}
                </View>
              )}
              onPress={() => {
                setProfileMenuVisible((p) => {
                  contextMenu.setVisible(!p);
                  return !p;
                });
                contextMenu.setIgnoreRef(profileButtonRef);
                // Measure fresh on press to get accurate position
                profileButtonRef.current?.measureInWindow((x, y) => {
                  contextMenu.setLocation({ x, y: y - 100 } as any);
                  contextMenu.setMenuContent(profileMenuContent);
                });
              }}
            />
          </View>
        </DrawerContentScrollView>
      )}
      screenOptions={{
        drawerType: isDesktopLayout ? "permanent" : "slide",
        sceneStyle: {
          borderColor: colors.border,
        },
        drawerStyle: { maxWidth: hideLabels ? 79 : undefined, backgroundColor: colors.background },
        drawerLabel: hideLabels ? "" : undefined,
        drawerLabelStyle: { marginLeft: 16, fontSize: 22 },
        drawerActiveTintColor: colors.primary,
        headerShown: !isDesktopLayout,
        header: ({ navigation }) => (
          <View style={{ flexDirection: "row", gap: 8, padding: 8, borderWidth: 0 }}>
            <Pressable
              onPress={() => {
                navigation.dispatch(DrawerActions.openDrawer());
              }}
            >
              <Image source={[logo, { width: 100 }]} style={{ height: 34, width: 34, marginLeft: 12 }} />
            </Pressable>
          </View>
        ),
        headerShadowVisible: false,
      }}
      layout={({ children }) => (
        <View
          onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
          style={{
            flex: 1,
            flexDirection: "row",
            backgroundColor: colors.background,
            alignContent: "center",
            justifyContent: "center",
          }}
        >
          <View
            style={{
              flex: 1,
              flexDirection: "row",
              backgroundColor: colors.background,
              alignContent: "center",
              maxWidth: 1600,
            }}
          >
            {children}
            {!hideRightPanel && (
              // right side panel
              <View
                style={{
                  flex: 1,
                  gap: 16,
                  padding: 8,
                  maxWidth: 400,
                  width: 400,
                  minWidth: 275,
                  borderColor: "red",
                }}
              >
                <Search value="" setValue={() => ""} />
                <View
                  style={{
                    flex: 1,
                    flexShrink: 1,
                    borderWidth: StyleSheet.hairlineWidth,
                    borderColor: colors.border,
                    borderRadius: 8,
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 24,
                  }}
                >
                  <View
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: 32,
                      backgroundColor: colors.emerald?.background || colors.primary + "15",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 16,
                    }}
                  >
                    <MaterialCommunityIcons
                      name="check-circle-outline"
                      size={36}
                      color={colors.emerald?.text || colors.primary}
                    />
                  </View>
                  <ThemedText type="defaultSemiBold" style={{ fontSize: 18, marginBottom: 8, textAlign: "center" }}>
                    You&apos;re All Caught Up!
                  </ThemedText>
                  <ThemedText style={{ color: colors.textSecondary, fontSize: 14, textAlign: "center", opacity: 0.7 }}>
                    No new activity right now
                  </ThemedText>
                </View>
              </View>
            )}
          </View>
        </View>
      )}
    >
      <Drawer.Screen
        name="home"
        options={{
          title: shiftsLabel,
          drawerIcon: ({ color }: any) => <IconSymbol size={30} name="home" color={color} />,
        }}
      />
      <Drawer.Screen
        name="supervising"
        options={{
          title: "Supervising",
          drawerIcon: ({ color }: any) => <IconSymbol size={30} name="shield" color={color} />,
        }}
      />
      <Drawer.Screen
        name="reports"
        options={{
          title: "Reports",
          drawerIcon: ({ color }: any) => <IconSymbol size={30} name="clipboard-text-outline" color={color} />,
        }}
      />
      <Drawer.Screen
        name="learning"
        options={{
          title: "Learning",
          drawerIcon: ({ color }: any) => <IconSymbol size={30} name="school" color={color} />,
        }}
      />
      <Drawer.Screen
        name="messaging"
        options={{
          title: "Messaging",
          drawerIcon: ({ color }: any) => <IconSymbol size={30} name="message" color={color} />,
          drawerItemStyle: { display: "none" },
        }}
      />
      <Drawer.Screen
        name="preferences"
        options={{
          title: "Settings",
          drawerIcon: ({ color }: any) => <IconSymbol style={{ padding: 0 }} size={30} name="cog" color={color} />,
        }}
      />
      <Drawer.Screen
        name="about"
        options={{
          title: "About",
          drawerIcon: ({ color }: any) => <IconSymbol size={30} name="info" color={color} />,
        }}
      />
    </Drawer>
  );
}
